import React from 'react';

import { Button, Grid } from '@material-ui/core';
import * as Sentry from '@sentry/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { ErrorBoundary } from 'react-error-boundary';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { mealPlanApi } from '../api';
import { MealPlanResponse } from '../api/generated/models';
import { LoadingSpinner } from '../components/loadingSpinner';
import { useStore } from '../context';
import { Trans, useTranslation } from '../i18n';
import { MealPlanIntakeForm } from './patient-meal-plan-intake';
import { MealPlanViewer } from './patient-meal-plan-viewer';
import './patient-meal-plans.scss';

/* eslint-disable i18next/no-literal-string */
export const statusBadgeClass = (status: string) => {
  switch (status) {
    case 'generating':
      return 'status-pill status-pill--generating';
    case 'generated':
      return 'status-pill status-pill--generated';
    case 'finalized':
      return 'status-pill status-pill--finalized';
    default:
      return 'status-pill status-pill--draft';
  }
};
/* eslint-enable i18next/no-literal-string */

const MealPlanCard = ({ plan, onDelete }: { plan: MealPlanResponse, onDelete: (id: number) => void }) => {
  const history = useHistory();
  const match = useRouteMatch();
  const { t } = useTranslation();

  const handleClick = () => {
    history.push(`${match.url}/${plan.id}`);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(plan.id);
  };

  const createdDate = plan.created_time
    ? DateTime.fromISO(plan.created_time).toLocaleString(DateTime.DATE_MED)
    : '';

  return (
    <div className="planCard" onClick={handleClick}>
      <div className="cardHeader">
        <h3 className="planTitle">{t('{{numDays}}-Day Meal Plan', { numDays: plan.num_days })}</h3>
        <button className="deleteBtn" onClick={handleDelete} title={t('delete')}>
          <i className="fas fa-trash-alt" />
        </button>
      </div>
      <span className={statusBadgeClass(plan.status)}>{t(plan.status)}</span>
      <div className="planMeta">
        {createdDate && <div>{createdDate}</div>}
        <div>
          {t('{{count}} days', { count: plan.num_days })}
          {/* eslint-disable-next-line i18next/no-literal-string */}
          {plan.target_calories != null && ` · ${plan.target_calories} ${t('kcal')}`}
        </div>
      </div>
    </div>
  );
};

const MealPlanList = () => {
  const { patient } = useStore();
  const { t } = useTranslation();
  const history = useHistory();
  const match = useRouteMatch();
  const queryClient = useQueryClient();

  const query = useQuery(
    ['meal-plans', patient?.patient_id],
    async () => {
      const res = await mealPlanApi.appApiMealPlanGetMealPlans({
        patient_id: patient.patient_id,
      });
      return res.data;
    },
    {
      enabled: !!patient?.patient_id,
      refetchInterval: (data) => data?.some((p: any) => p.status === 'generating') ? 3000 : false,
    },
  );

  const handleDelete = async (mealPlanId: number) => {
    if (!window.confirm(t('Are you sure you wish to delete this meal plan?'))) {
      return;
    }
    try {
      await mealPlanApi.appApiMealPlanDeleteMealPlan({
        patient_id: patient.patient_id,
        meal_plan_id: mealPlanId,
      });
      // eslint-disable-next-line i18next/no-literal-string
      queryClient.invalidateQueries(['meal-plans', patient.patient_id]);
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const handleCreate = () => {
    history.push(`${match.url}/new`);
  };

  const plans = query.data as MealPlanResponse[] | undefined;

  return (
    <ErrorBoundary
      fallbackRender={() => (
        <div>
          <Trans>Error loading meal plans</Trans>
        </div>
      )}
      onError={(error) => Sentry.captureException(error)}
    >
      <Grid container style={{ marginTop: 30, marginBottom: 30 }}>
        <Grid item md={12}>
          <div className="mealPlansPage">
            <div className="pageHeader">
              <h2>
                <Trans>Meal Plans</Trans>
              </h2>
              <Button variant="contained" color="primary" onClick={handleCreate}>
                <Trans>Create Meal Plan</Trans>
              </Button>
            </div>

            {query.isLoading && <LoadingSpinner />}

            {query.isSuccess && (!plans || plans.length === 0) && (
              <div className="emptyState">
                <p>
                  <Trans>No meal plans yet</Trans>
                </p>
                <Button variant="outlined" color="primary" onClick={handleCreate}>
                  <Trans>Create Meal Plan</Trans>
                </Button>
              </div>
            )}

            {query.isSuccess && plans && plans.length > 0 && (
              <div className="planCards">
                {plans.map((plan) => <MealPlanCard key={plan.id} plan={plan} onDelete={handleDelete} />)}
              </div>
            )}
          </div>
        </Grid>
      </Grid>
    </ErrorBoundary>
  );
};

export const MealPlans = () => {
  const match = useRouteMatch();
  return (
    <Switch>
      <Route path={`${match.path}/new`} component={MealPlanIntakeForm} />
      <Route path={`${match.path}/:mealPlanId`} component={MealPlanViewer} />
      <Route path={match.path} exact component={MealPlanList} />
    </Switch>
  );
};
